import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface PackageChangesTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}


export function PackageChangesTable({ changes, isLoading }: PackageChangesTableProps) {
  const [packageNames, setPackageNames] = useState<Record<number, string>>({});

  // Filter to only show packages entity_type
  const packageChanges = changes?.filter(change =>
    change.entity_type === 'packages'
  ) || [];

  // Get unique package IDs
  const packageIds = [...new Set(packageChanges.map(c => c.entity_id))];

  // Fetch package names from database and extract from deleted entity data
  useEffect(() => {
    const fetchPackageNames = async () => {
      if (packageIds.length === 0) return;

      // First try to get names from the packages table for existing packages
      const { data, error } = await (supabase
        .from('packages' as any)
        .select('id, name')
        .in('id', packageIds) as any);

      const nameMap: Record<number, string> = {};

      // Add names for existing packages
      if (!error && data) {
        data.forEach((pkg: any) => {
          nameMap[pkg.id] = pkg.name;
        });
      }

      // For packages not found in the database (deleted), extract names from change log old_value
      const missingPackageIds = packageIds.filter(id => !nameMap[id]);
      if (missingPackageIds.length > 0) {
        missingPackageIds.forEach(packageId => {
          // Find a change entry for this package that has old_value with entity data
          const entityChange = packageChanges.find(change =>
            change.entity_id === packageId &&
            change.field_name === 'entity' &&
            change.old_value
          );

          if (entityChange?.old_value) {
            try {
              const entityData = JSON.parse(entityChange.old_value);
              nameMap[packageId] = entityData.name || `Package #${packageId}`;
            } catch (error) {
              console.error('Failed to parse entity data for package:', packageId, error);
              nameMap[packageId] = `Package #${packageId}`;
            }
          } else {
            nameMap[packageId] = `Package #${packageId}`;
          }
        });
      }

      setPackageNames(nameMap);
    };

    fetchPackageNames();
  }, [packageIds.join(','), packageChanges]);

  // Group changes by package and then by actual action type
  const groupedByPackage = packageChanges.reduce((acc, change) => {
    const packageId = change.entity_id;

    if (!acc[packageId]) {
      acc[packageId] = {
        packageId,
        packageName: packageNames[packageId] || `Package #${packageId}`,
        createFields: {},
        updateFields: {},
        deleteFields: {},
        createTimestamp: null,
        updateTimestamp: null,
        deleteTimestamp: null,
        latestTimestamp: change.created_at
      };
    }

    // Track latest timestamp for this package
    if (new Date(change.created_at) > new Date(acc[packageId].latestTimestamp)) {
      acc[packageId].latestTimestamp = change.created_at;
    }

    // Handle entity-level operations (whole package created/deleted)
    if (change.field_name === 'entity') {
      if (change.old_value === null && change.new_value !== null) {
        // Entire package was created
        acc[packageId].isEntityCreated = true;
        acc[packageId].entityCreatedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.new_value);
          acc[packageId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity creation data:', error);
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Entire package was deleted
        acc[packageId].isEntityDeleted = true;
        acc[packageId].entityDeletedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.old_value);
          acc[packageId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity deletion data:', error);
        }
      }
    } else {
      // Handle field-level changes
      if (change.old_value === null && change.new_value !== null) {
        // Field was created (added)
        acc[packageId].createFields[change.field_name] = {
          value: change.new_value
        };
        if (!acc[packageId].createTimestamp || new Date(change.created_at) > new Date(acc[packageId].createTimestamp!)) {
          acc[packageId].createTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Field was deleted (removed)
        acc[packageId].deleteFields[change.field_name] = {
          value: change.old_value
        };
        if (!acc[packageId].deleteTimestamp || new Date(change.created_at) > new Date(acc[packageId].deleteTimestamp!)) {
          acc[packageId].deleteTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value !== null) {
        // Field was updated
        if (!acc[packageId].updateFields[change.field_name]) {
          acc[packageId].updateFields[change.field_name] = {
            oldValue: change.old_value,
            newValue: change.new_value
          };
        } else {
          // If field was already updated, keep the original old value but update the new value
          acc[packageId].updateFields[change.field_name].newValue = change.new_value;
        }
        if (!acc[packageId].updateTimestamp || new Date(change.created_at) > new Date(acc[packageId].updateTimestamp!)) {
          acc[packageId].updateTimestamp = change.created_at;
        }
      }
    }

    return acc;
  }, {} as Record<number, {
    packageId: number;
    packageName: string;
    createFields: Record<string, { value: string }>;
    updateFields: Record<string, { oldValue: string; newValue: string }>;
    deleteFields: Record<string, { value: string }>;
    createTimestamp: string | null;
    updateTimestamp: string | null;
    deleteTimestamp: string | null;
    latestTimestamp: string;
    isEntityCreated?: boolean;
    isEntityDeleted?: boolean;
    entityCreatedAt?: string;
    entityDeletedAt?: string;
    entityData?: any;
  }>);

  if (isLoading) return <div>Loading...</div>;

  // Convert to array and sort
  const sortedPackages = Object.values(groupedByPackage).sort((a, b) =>
    new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
  );

  const renderFieldValue = (
    actionType: 'CREATE' | 'UPDATE' | 'DELETE',
    _fieldName: string,
    fieldData: any
  ) => {
    if (!fieldData) return <span className="text-muted-foreground">-</span>;

    // Regular field handling
    if (actionType === 'CREATE') {
      return (
        <div className="text-xs text-green-600 font-medium">
          {fieldData.value || 'null'}
        </div>
      );
    } else if (actionType === 'UPDATE') {
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-600 line-through">
            {fieldData.oldValue || 'null'}
          </div>
          <div className="text-xs text-green-600 font-medium">
            {fieldData.newValue || 'null'}
          </div>
        </div>
      );
    } else if (actionType === 'DELETE') {
      return (
        <div className="text-xs text-red-600 line-through">
          {fieldData.value || 'null'}
        </div>
      );
    }

    return <span className="text-muted-foreground">-</span>;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Package Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Type</TableHead>
          <TableHead className="font-semibold">Monthly Price</TableHead>
          <TableHead className="font-semibold">Yearly Price</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Timestamp</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPackages.map((pkg) => {
          // Build array of actions to display
          const actions = [];

          // Add ENTITY_CREATED row if the entire package was created
          if (pkg.isEntityCreated) {
            actions.push({
              type: 'ENTITY_CREATED' as const,
              fields: {},
              timestamp: pkg.entityCreatedAt,
              entityData: pkg.entityData
            });
          }

          // Add ENTITY_DELETED row if the entire package was deleted
          if (pkg.isEntityDeleted) {
            actions.push({
              type: 'ENTITY_DELETED' as const,
              fields: {},
              timestamp: pkg.entityDeletedAt,
              entityData: pkg.entityData
            });
          }

          // Add CREATE row if there are created fields
          if (Object.keys(pkg.createFields).length > 0) {
            actions.push({
              type: 'CREATE' as const,
              fields: pkg.createFields,
              timestamp: pkg.createTimestamp
            });
          }

          // Add UPDATE row if there are updated fields
          if (Object.keys(pkg.updateFields).length > 0) {
            actions.push({
              type: 'UPDATE' as const,
              fields: pkg.updateFields,
              timestamp: pkg.updateTimestamp
            });
          }

          // Add DELETE row if there are deleted fields
          if (Object.keys(pkg.deleteFields).length > 0) {
            actions.push({
              type: 'DELETE' as const,
              fields: pkg.deleteFields,
              timestamp: pkg.deleteTimestamp
            });
          }

          if (actions.length === 0) return null;

          return actions.map((action, index) => {
            const isFirstRow = index === 0;

            return (
              <TableRow key={`${pkg.packageId}-${action.type}`} className="hover:bg-muted/50">
                {/* Package Name - merged cell for first row only */}
                {isFirstRow && (
                  <TableCell
                    rowSpan={actions.length}
                    className="font-medium align-top border-r"
                  >
                    <div className="font-semibold">{pkg.packageName}</div>
                  </TableCell>
                )}

                {/* Action */}
                <TableCell>
                  <Badge className={
                    action.type === 'ENTITY_CREATED' ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 font-bold" :
                    action.type === 'ENTITY_DELETED' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 font-bold" :
                    action.type === 'CREATE' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                    action.type === 'UPDATE' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100" :
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                  }>
                    {action.type === 'ENTITY_CREATED' ? 'PACKAGE CREATED' :
                     action.type === 'ENTITY_DELETED' ? 'PACKAGE DELETED' :
                     action.type}
                  </Badge>
                </TableCell>

                {/* Name */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs font-medium ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.name || '-'}
                      </div>
                    : renderFieldValue(action.type, 'name', action.fields['name'])}
                </TableCell>

                {/* Type */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.package_type || '-'}
                      </div>
                    : renderFieldValue(action.type, 'package_type', action.fields['package_type'])}
                </TableCell>

                {/* Monthly Price */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.monthly_price ? `$${action.entityData.monthly_price}` : '-'}
                      </div>
                    : renderFieldValue(action.type, 'monthly_price', action.fields['monthly_price'])}
                </TableCell>

                {/* Yearly Price */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.yearly_price ? `$${action.entityData.yearly_price}` : '-'}
                      </div>
                    : renderFieldValue(action.type, 'yearly_price', action.fields['yearly_price'])}
                </TableCell>

                {/* Description */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'} max-w-xs truncate`}>
                        {action.entityData?.description || '-'}
                      </div>
                    : renderFieldValue(action.type, 'description', action.fields['description'])}
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  <div className="text-xs">{action.timestamp ? formatDateTime(action.timestamp) : '-'}</div>
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  );
}
